/**
 * LA FIRMA DEL PROFESIONAL, cargada una vez.
 *
 * EL RODEO QUE BORRA
 *
 * Para que una receta saliera firmada había que imprimirla, firmarla a mano,
 * fotografiarla y volver a subirla. Cuatro pasos y un escáner por cada receta
 * de cada paciente de cada día, para poner la misma firma que ya se puso mil
 * veces. Aquí se sube una vez y el sistema la estampa en todas.
 *
 * LO QUE NO SUSTITUYE
 *
 * Se sigue pudiendo descargar la receta SIN firma, para rubricarla de puño:
 * hay farmacias que no aceptan una estampada. Esto no quita esa opción, quita
 * el trabajo de repetirla cuando no hace falta.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PenLine, Trash2, Upload } from "lucide-react";
import {
  borrarMiFirma,
  miFirmaBlob,
  subirMiFirma,
} from "../api/profesionales";
import { Alert, Button, Card, CardBody, CardHeader, ConfirmDialog } from "./ui";

export function MiFirmaCard() {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [porQuitar, setPorQuitar] = useState(false);

  async function recargar() {
    setCargando(true);
    const nueva = await miFirmaBlob();
    // El blob anterior se libera a mano: sin esto, cada recarga deja una copia
    // de la imagen retenida en memoria hasta que se cierre la pestaña.
    setUrl((previa) => {
      if (previa) URL.revokeObjectURL(previa);
      return nueva;
    });
    setCargando(false);
  }

  useEffect(() => {
    void recargar();
    // Y al desmontar, soltar la última.
    return () => setUrl((previa) => {
      if (previa) URL.revokeObjectURL(previa);
      return null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subir = useMutation({
    mutationFn: (f: File) => subirMiFirma(f),
    onSuccess: async () => {
      setError(null);
      await recargar();
      // Las recetas ya emitidas pasan a salir firmadas: sus PDFs se generan al
      // vuelo, así que basta con que la próxima descarga lea la firma nueva.
      queryClient.invalidateQueries({ queryKey: ["recetas"] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const borrar = useMutation({
    mutationFn: () => borrarMiFirma(),
    onSuccess: async () => {
      setPorQuitar(false);
      setError(null);
      await recargar();
    },
    onError: (e: Error) => {
      setPorQuitar(false);
      setError(e.message);
    },
  });

  return (
    <Card>
      <CardHeader>
        <h2 className="flex items-center gap-2 font-semibold text-slate-900">
          <PenLine className="h-4 w-4 text-[color:var(--color-link)]" aria-hidden="true" />
          Mi firma
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Se estampa sola en las recetas que emitas. Súbela una vez.
        </p>
      </CardHeader>
      <CardBody className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {cargando ? (
          <p className="text-sm text-slate-400">Comprobando…</p>
        ) : url ? (
          <>
            {/* Fondo cuadriculado: una firma en PNG transparente sobre blanco
                parece recortada a lo bruto, y sobre el cuadro se ve qué parte
                es transparente de verdad — que es lo que determina si al
                estamparla tapará la raya. */}
            <div
              className="flex items-center justify-center rounded-lg border border-slate-200 p-4"
              style={{
                backgroundImage:
                  "repeating-conic-gradient(#f1f5f9 0% 25%, white 0% 50%)",
                backgroundSize: "16px 16px",
              }}
            >
              <img
                src={url}
                alt="Tu firma"
                className="max-h-24 object-contain"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <Upload className="h-4 w-4" />
                Cambiarla
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) subir.mutate(f);
                    e.target.value = "";
                  }}
                />
              </label>
              <Button variant="ghost" onClick={() => setPorQuitar(true)}>
                <Trash2 className="h-4 w-4" />
                Quitarla
              </Button>
            </div>
          </>
        ) : (
          <>
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 px-4 py-8 text-center hover:border-[color:var(--color-link)] hover:bg-slate-50">
              <Upload className="h-6 w-6 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">
                Sube una foto de tu firma
              </span>
              <span className="text-xs text-slate-500">
                PNG o JPG, máximo 2 MB. En PNG con fondo transparente queda
                mejor sobre la línea.
              </span>
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) subir.mutate(f);
                  e.target.value = "";
                }}
              />
            </label>
            <p className="text-xs text-slate-500">
              Sin firma cargada, las recetas salen con la línea vacía para que
              la firmes a mano.
            </p>
          </>
        )}

        {subir.isPending && (
          <p className="text-sm text-slate-500">Subiendo…</p>
        )}
      </CardBody>

      <ConfirmDialog
        open={porQuitar}
        onClose={() => setPorQuitar(false)}
        onConfirm={() => borrar.mutate()}
        loading={borrar.isPending}
        title="Quitar tu firma"
        description="Las recetas volverán a salir con la línea vacía para firmarlas a mano. Puedes subirla otra vez cuando quieras."
        confirmLabel="Sí, quitarla"
        cancelLabel="No, dejarla"
      />
    </Card>
  );
}
